import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useCartSync } from "@/hooks/useCartSync";
import { useKlaviyoInit } from "@/hooks/useKlaviyo";
import { useShopifySync } from "@/hooks/useShopifySync";
import { useState, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

import { ReviewRequestPopup } from "@/components/ReviewRequestPopup";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ScrollToTopButton } from "@/components/ScrollToTopButton";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { SearchModal } from "@/components/SearchModal";

// Eager load critical pages
import Index from "./pages/Index";
// Lazy load non-critical pages for performance
const ProductPage = lazy(() => import("./pages/ProductPage"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ShippingPage = lazy(() => import("./pages/ShippingPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const DeliveryTermsPage = lazy(() => import("./pages/DeliveryTermsPage"));
const KopThcaSverigePage = lazy(() => import("./pages/KopThcaSverigePage"));
const ThcaSverigePage = lazy(() => import("./pages/ThcaSverigePage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Redirect components for legacy Shopify URLs
const RedirectToBlogg = () => {
  const pathname = window.location.pathname;
  const newPath = pathname.replace('/blogs/news/', '/blogg/').replace('/blogs/', '/blogg/');
  window.location.href = newPath;
  return null;
};

const RedirectToPage = ({ to }: { to: string }) => {
  window.location.href = to;
  return null;
};

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

function AppRoutes() {
  const [searchOpen, setSearchOpen] = useState(false);
  
  useCartSync();
  useKlaviyoInit(); // Initialize Klaviyo tracking
  useShopifySync(); // Real-time Shopify sync via webhooks
  
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/product/:handle" element={<ProductPage />} />
          <Route path="/kollektioner/:collection" element={<CollectionPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/blogg" element={<BlogPage />} />
          <Route path="/blogg/:slug" element={<BlogPostPage />} />
          <Route path="/om-oss" element={<AboutPage />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/frakt-leverans" element={<ShippingPage />} />
          <Route path="/sok" element={<SearchPage />} />
          <Route path="/konto" element={<AccountPage />} />
          <Route path="/onskelista" element={<WishlistPage />} />
          <Route path="/returer" element={<ReturnsPage />} />
          <Route path="/betalning" element={<PaymentPage />} />
          <Route path="/spara-order" element={<TrackOrderPage />} />
          <Route path="/integritetspolicy" element={<PrivacyPage />} />
          <Route path="/kopvillkor" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/leveransvillkor" element={<DeliveryTermsPage />} />
          <Route path="/kop-thca-sverige" element={<KopThcaSverigePage />} />
          <Route path="/thca-sverige" element={<ThcaSverigePage />} />
          
          {/* Legacy Shopify URL redirects - MUST come before catch-all */}
          <Route path="/collections/:collection" element={<CollectionPage />} />
          <Route path="/blogs/*" element={<RedirectToBlogg />} />
          <Route path="/pages/about" element={<RedirectToPage to="/om-oss" />} />
          <Route path="/pages/about-us" element={<RedirectToPage to="/om-oss" />} />
          <Route path="/pages/contact" element={<RedirectToPage to="/kontakt" />} />
          <Route path="/pages/contact-us" element={<RedirectToPage to="/kontakt" />} />
          <Route path="/pages/faq" element={<RedirectToPage to="/faq" />} />
          <Route path="/pages/shipping" element={<RedirectToPage to="/frakt-leverans" />} />
          <Route path="/pages/delivery" element={<RedirectToPage to="/frakt-leverans" />} />
          <Route path="/pages/returns" element={<RedirectToPage to="/returer" />} />
          <Route path="/pages/privacy" element={<RedirectToPage to="/integritetspolicy" />} />
          <Route path="/pages/privacy-policy" element={<RedirectToPage to="/integritetspolicy" />} />
          <Route path="/pages/terms" element={<RedirectToPage to="/kopvillkor" />} />
          <Route path="/pages/terms-of-service" element={<RedirectToPage to="/kopvillkor" />} />
          <Route path="/account" element={<RedirectToPage to="/konto" />} />
          <Route path="/account/login" element={<RedirectToPage to="/konto" />} />
          <Route path="/account/register" element={<RedirectToPage to="/konto" />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      
      {/* Mobile bottom navigation */}
      <MobileBottomNav onSearchClick={() => setSearchOpen(true)} />
      
      {/* Global search modal */}
      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}

const App = () => {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 30, // 30 seconds - faster sync for product data
        gcTime: 1000 * 60 * 10, // 10 minutes cache retention
        retry: 1,
        refetchOnWindowFocus: true, // Refresh when user returns to tab
      },
    },
  }));

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        
        <BrowserRouter>
          <ScrollToTop />
          <ScrollToTopButton />
          <ReviewRequestPopup />
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
